import { gridMap } from '../mapgrid.ts';
import { withGeometry } from '../mapgeo.ts';
import { OLDTOWN_GEOMETRY } from './oldtowngeo.ts';

/**
 * Old Town: a walled European quarter at dusk. A canal cuts across the north with three stone bridges; St. Aldric's church and its
 * square (zone A, with the fountain) face the West Gate where red comes in; the market square sits on the centre point (zone B)
 * between the coffee house and the cloth hall; and round them run lanes of half-timbered houses, each furnished inside: the
 * Crooked Tankard and its beer garden, the bakery, the weaver's, the apothecary, the guild hall, the laundry and its drying yard,
 * the merchant's house over its archway, the cooperage, the stables and the post house. The west half is drawn here and the
 * east half is its half turn; the theme (src/client/themes/oldtown*.ts) dresses each twin as a different place.
 *
 * @ half-timbered house wall, $ dressed stone (church, quay, sluice, town wall), * furniture (tables, beds, shelves, counters),
 * ! stalls, fences and benches. The canal, the church's apse and tower, the gate towers, the fountain, the well, the carts and
 * the trees are polygons (oldtowngeo.ts).
 */
const OLDTOWN_GRID = gridMap('Old Town', `
  55555555555555....555555555555....5555555555..555555..555555
  5*...........5....5666..***.*5....5.***..**5..5**.*5..5*.**5
  5............5....5666......*5....5......**5..5**.*5..5*.**5
  5.*******....5....5666......*5....5......**5..5**.*5..5..**5
  5.................5..........5.............5..5....5..5....5
  5.................5..........5.........**..5..5....5.......5
  5..**...**...5.........******5....5....**..5..5....5.......5
  5..**...**..65...............5....5*.......5..5.*..5..5....5
  5...........65....5..........5....5*.......5..5.*..5..5....5
  5............5....5......**..5....555..55555..5....5..55..55
  555555..555555....5..........5................5....5........
  ..................5555..555555................5....5........
  ................p.............................5....5........
  ...............8..............................55..55........
  ..***....***...8..................FFFFF.....................
  ...............8..................FFFFF.....................
  ...............................cc.FFFFF................888..
  ..................................FFFFF................888..
  ..***....***...8..................FFFFF.....b...............
  ...............8..................FFFFF.....................
  ...............8............................................
  8888....88888888............................................
  .................l.........................l.........l......
  ..........................................................66
  .....6....6..............6....6..............6....6.......66
  .....6....6..............6....6..............6....6.......66
  .....6....6..............6....6..............6....6.......66
  .....6....6..............6....6..............6....6.......66
  ..........................................................66
  ..............................g.............................
  ..FFFFF.c..............................5555555555..5555555..
  ..FFFFF................................5.***...*5..5*...*5..
  ..FFFFF........................................*5..5*...*5..
  ..FFFFF........................................*5..5....*5..
  ..FFFFF...66666666666666..66666........5.......*5........5..
  ..FFFFF...6............................5..****..5........5..
  ..........6............................5........5..5.***.5..
  ..........6............................5........5..5.***.5..
  ..........6............................5........5..5.....5..
  .......................................5555..5555..5.....5..
  ...................................................5.....5..
  ..........6........................................55..555..
  ...l....666.................................................
  ........666.................................................
  ........666....................................5555555555555
  ........6666666666..66666666666................5...........5
  ........66........................................**...**..5
  ........66...FFFFF....l...........................**...**..5
  ........66...FFFFF.....................A.......5...........5
  ........66...FFFFF.............................5...........5
  ........66...FFFFF.............................55555..555555
  .............FFFFF..........................................
  ............................................l...............
  .XXXXXX.........................555555555555..888......888..
  .XXXXXX.........................5........**5................
  .XXXXXX..................................**5................
  .XXXXXX......................l......***.....................
  .XXXXXX.....555..555555.........5...***.....................
  .XXXXXX.....5.........5.........5**........5.......88.......
  .XXXXXX.....5.**...**.5.........555555555555.......88.......
  .XXXXXX.....5.**......5............................88.......
  .XXXXXX.....5......................................88.......
  .XXXXXX.....5...................55555..55555................
  .XXXXXX.....5.**......5.........5..........5................
  .XXXXXX.....5.**.....*5.........5.**........................
  .XXXXXX.....5........*5.........5.**........................
  .XXXXXX.....5........*5.........5......****5..888......888..
  ............5555555..55.........5..........5................
  ................................555555555555................
  ........66..................................l...............
  ........66..................................................
  ........66..................................................
  ........66....................................5555..555555..
  ..............................................5..........5..
  5555..55555...5555..55555.......5555...55555..5.......**.5..
  5**......*5...5**.......5.b.....5..........5..........**.5..
  5**......*5...5**.......5..b..e.5..........5.....****....5..
  5**.......5...5.........5.......5..........5..5.........*5..
  5.............5.................5***....***5..5.........*5..
  5.............5.................5..........5..5.........*5..
  5....**...5...5**.......5.......5..........5..555555555555..
  5....**...5...5**...***.5.......5***....***5................
  5.........5...5**.......5.......5..........5................
  5.........5...55555555555.......5..........5................
  55555555555.....................55555555..55..555555555555..
  ..............................................5........**5..
  ............................l..........................**5..
  .......................................................**5..
  55555555555...................888.............5..........5..
  5**.......5...................................5.***......5..
  5**.......5.........FFFFF.....................555555..5555..
  5**.......5.........FFFFF...................................
  5...................FFFFF...................................
  5...................FFFFF............6666....o..............
  5...**....5.........FFFFF...................................
  5...**....5.........FFFFF.....m..................55555..5555
  5........*5......................................5.**......5
  5........*5........................................**......5
  55555..5555...............888..............................5
  .................................................5.......**5
  ..............l..................................5.......**5
  .........i.......................................55555555555
  ...................................a...........l............
  ............................................................
  55555..555555....................................555555555..
  5...........5.........5555..555555...............5.....**5..
  5.***.......5.........5**........5...............5.....**5..
  5...........5.........5**........5...555..55555........**5..
  5.......**..5..FFFFF..5**........5...5......**5..........5..
  5.......**..5..FFFFF..5..........5...5......**5..5.......5..
  5..............FFFFF..5..............5......**5..5.**....5..
  5..............FFFFF..5..............5........5..5.**....5..
  5...........5..FFFFF..5.....**...5............5..5.......5..
  5**.........5..FFFFF..5.....**...5............5..5555..555..
  5**.........5..FFFFF..5..........5...5........5.............
  5**.........5.........5.........*5...5.**.....5...FFFFFF....
  5......****.5.........5.........*5...5.**.....5...FFFFFF....
  5...........5.........5.........*5...5........5...FFFFFF....
  5...........5.........5..........5...5........5...FFFFFF....
  5555555555555.........555555555555...5555555555.............
`, 'oldtown');

export const OLDTOWN = withGeometry(OLDTOWN_GRID, OLDTOWN_GEOMETRY);
